import { endIdentityTrace } from "@/lib/wallet/identity-trace";
import {
  injectedIdentity,
  identityAttempt,
  identityPhase,
} from "@/lib/wallet/identity-diagnostics";
import { instrumentSignIn, invokeSignIn } from "@/lib/wallet/sign-in-telemetry";

import type { IdentityAttempt } from "@/lib/wallet/identity-diagnostics";

/**
 * Wallet Standard's `solana:signIn`, read off the injected provider.
 *
 * `PhantomWalletAdapter` extends `BaseMessageSignerWalletAdapter` and does
 * not expose `signIn`, so this reaches the same object the adapter itself
 * reaches (`window.phantom?.solana || window.solana`). That is worth doing
 * rather than signing a message after connecting, for two reasons:
 *
 * - It is one approval instead of two. `signIn` connects and authenticates in
 *   the same prompt, and the adapter's own `connect()` then finds the wallet
 *   already connected and asks for nothing.
 * - Phantom validates a SIWS-shaped message handed to plain `signMessage` and
 *   refuses it when the chain named disagrees with the holder's cluster. Going
 *   through `signIn` hands it structured input and lets it render its own
 *   text, which is what the spec intends.
 *
 * Everything here is guarded: a wallet without the feature answers `null` and
 * the caller connects the ordinary way.
 */
/**
 * Deliberately only the nonce and the statement.
 *
 * Phantom refuses an input carrying `domain` outright — "the app's signature
 * request cannot be shown due to invalid formatting" — and refuses before it
 * shows anything, so the holder never sees a prompt at all. Found against the
 * real extension; no unit test reaches it.
 *
 * Leaving it out is the better design regardless. Phantom fills the domain
 * and URI in from the page's own origin, which it will not let a site
 * misstate, so the signed text names where the holder actually was rather
 * than where we claimed they were. The server checks that line against the
 * domain it issued the challenge for, so nothing is given up.
 *
 * The expiry is not sent either: it is enforced on the challenge row, which
 * is where it has to hold anyway.
 */
export interface SignInInput {
  nonce: string;
  statement: string;
}

export interface SignInResult {
  address: string;
  signedMessage: Uint8Array;
  signature: Uint8Array;
}

export type SignIn = (input: SignInInput) => Promise<unknown>;

/**
 * Where a sign-in is asked for: a wallet's `signIn`, or null without one.
 * Phantom's injected provider by default; the generic layer passes the
 * chosen Wallet Standard wallet's (`solana/sign-in.ts`).
 */
export type SignInSource = () => SignIn | null;

function isSignIn(value: unknown): value is SignIn {
  return typeof value === "function";
}

/** The same object the adapter reaches, with its `signIn` bound to it. */
function injected(): SignIn | null {
  if (typeof window === "undefined") return null;
  const phantom: unknown = Reflect.get(window, "phantom");
  const solana: unknown =
    (typeof phantom === "object" && phantom !== null
      ? Reflect.get(phantom, "solana")
      : undefined) ?? Reflect.get(window, "solana");
  if (typeof solana !== "object" || solana === null) return null;
  const signIn: unknown = Reflect.get(solana, "signIn");
  return isSignIn(signIn)
    ? instrumentSignIn(
        (input) => signIn.call(solana, input),
        injectedIdentity(solana)
      )
    : null;
}

export function supportsSignIn(source: SignInSource = injected): boolean {
  return source() !== null;
}

/**
 * The address, from either shape a wallet answers in.
 *
 * Wallet Standard returns `{ account: { address } }` with the address as a
 * base58 string. Phantom's injected provider returns a top-level `address`
 * holding a `PublicKey`, which has to be asked for its string. Reading only
 * the first shape is why the one-approval route quietly did nothing and the
 * second prompt appeared anyway.
 */
function readAddress(value: object): string | null {
  const account: unknown = Reflect.get(value, "account");
  const fromAccount: unknown =
    typeof account === "object" && account !== null
      ? Reflect.get(account, "address")
      : undefined;
  if (typeof fromAccount === "string") return fromAccount;

  const direct: unknown = Reflect.get(value, "address");
  if (typeof direct === "string") return direct;
  if (typeof direct === "object" && direct !== null) {
    const toBase58: unknown = Reflect.get(direct, "toBase58");
    if (typeof toBase58 === "function") {
      const encoded: unknown = toBase58.call(direct);
      if (typeof encoded === "string") return encoded;
    }
    const encoded = String(direct);
    if (encoded && encoded !== "[object Object]") return encoded;
  }
  return null;
}

/** Bytes, whether handed over as a view or as a plain array of numbers. */
function readBytes(value: unknown): Uint8Array | null {
  if (value instanceof Uint8Array) return value;
  if (Array.isArray(value) && value.every((n) => typeof n === "number"))
    return Uint8Array.from(value);
  return null;
}

/** Narrows the wallet's answer, which is typed as whatever it pleases. */
function readResult(value: unknown): SignInResult | null {
  if (typeof value !== "object" || value === null) return null;
  const address = readAddress(value);
  const signedMessage = readBytes(Reflect.get(value, "signedMessage"));
  const signature = readBytes(Reflect.get(value, "signature"));
  if (!address || !signedMessage || !signature) {
    // Naming the keys is the difference between a one-line fix and another
    // round of guessing at a shape only the extension can show us.
    console.warn(
      "[solana] the wallet answered signIn in an unreadable shape",
      Object.keys(value)
    );
    return null;
  }
  return { address, signedMessage, signature };
}

/** Null when the wallet has no `signIn`, or answers something unusable. */
export async function signInWithWallet(
  input: SignInInput,
  source: SignInSource = injected,
  attempt?: IdentityAttempt
): Promise<SignInResult | null> {
  const signIn = source();
  if (!signIn) return null;
  const diagnostics = attempt ?? identityAttempt("wallet");
  try {
    const result = readResult(await invokeSignIn(signIn, input, diagnostics));
    identityPhase(diagnostics, "validation_returned", {
      outcome: result ? "ok" : "invalid",
    });
    return result;
  } finally {
    if (!attempt) endIdentityTrace(diagnostics);
  }
}
